import { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LocalePicker from "components/Elements/LocalePicker";
import { localeOptions } from "helpers/localeOptions";

const DEFAULTS = {
	sourceTerm: "",
	targetTerm: "",
	context: "",
};

const findLocaleByValue = (value) =>
	localeOptions.find((opt) => opt.value === value) ?? null;

const AddEntryDialog = ({
	open,
	closeHandler,
	onSubmit,
	defaultLocale,
	editEntry,
}) => {
	const { t } = useTranslation();
	const isEdit = !!editEntry;
	const [values, setValues] = useState(DEFAULTS);
	const [locale, setLocale] = useState(null);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!open) return;
		if (editEntry) {
			setValues({
				sourceTerm: editEntry.sourceTerm ?? "",
				targetTerm: editEntry.targetTerm ?? "",
				context: editEntry.context ?? "",
			});
			setLocale(findLocaleByValue(editEntry.targetLanguage) ?? null);
		} else {
			setValues(DEFAULTS);
			setLocale(
				findLocaleByValue(defaultLocale) ??
					localeOptions.find((opt) => opt.language === "es") ??
					localeOptions[0] ??
					null
			);
		}
		setErrors({});
	}, [open, defaultLocale, editEntry]);

	const update = (field, value) =>
		setValues((prev) => ({ ...prev, [field]: value }));

	const validate = () => {
		const next = {};
		if (!values.sourceTerm.trim()) next.sourceTerm = t("validation.requiredShort");
		if (!values.targetTerm.trim()) next.targetTerm = t("validation.requiredShort");
		if (!locale) next.locale = t("validation.requiredShort");
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;
		onSubmit({
			sourceTerm: values.sourceTerm.trim(),
			targetTerm: values.targetTerm.trim(),
			targetLanguage: locale.value,
			context: values.context.trim() || null,
		});
	};

	return (
		<Dialog open={open} onClose={closeHandler} fullWidth maxWidth="sm">
			<DialogTitle>
				{isEdit
					? t("title.editGlossaryEntry")
					: t("title.addGlossaryEntry")}
			</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					fullWidth
					label={t("label.sourceTerm")}
					required
					value={values.sourceTerm}
					onChange={(e) => update("sourceTerm", e.target.value)}
					error={!!errors.sourceTerm}
					helperText={errors.sourceTerm || t("message.sourceTermHelper")}
					sx={{ mt: 1, mb: 2 }}
				/>
				<LocalePicker
					value={locale}
					onChange={setLocale}
					label={t("label.targetLocale")}
					required
					helperText={
						errors.locale || t("message.typeLanguageOrCountry")
					}
					sx={{ mb: 2 }}
				/>
				<TextField
					fullWidth
					label={t("label.targetTerm")}
					required
					value={values.targetTerm}
					onChange={(e) => update("targetTerm", e.target.value)}
					error={!!errors.targetTerm}
					helperText={
						errors.targetTerm || t("message.targetTermHelper")
					}
					sx={{ mb: 2 }}
				/>
				<TextField
					fullWidth
					label={t("label.context")}
					value={values.context}
					onChange={(e) => update("context", e.target.value)}
					placeholder={t("placeholder.optional")}
					helperText={t("message.contextHelper")}
					multiline
					minRows={2}
				/>
				<Typography variant="caption" sx={{ display: "block", mt: 2, color: "#666" }}>
					{t("message.glossaryPassThroughTip")}
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={closeHandler}>{t("action.cancel")}</Button>
				<Button variant="contained" onClick={handleSubmit}>
					{isEdit ? t("action.saveChangesLower") : t("action.addEntry")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddEntryDialog;
